<template>
  <div class="container-fluid py-4">
    <div class="row justify-content-center">
      <div class="col-lg-10 col-md-12">
        <div class="card">
          <div class="card-body p-4">

            <!-- Loading State -->
            <div v-if="loading" class="text-center py-4">
              <div class="spinner-border text-primary" role="status">
                <span class="visually-hidden">Loading...</span>
              </div>
              <p class="mt-2">Loading swap details...</p>
            </div>

            <!-- Content -->
            <template v-else>
              <!-- Swap Details Box -->
              <div class="border border-dark p-4 mb-4 text-sm rounded"
                v-if="displaySwapDetails && (displaySwapDetails.location || displaySwapDetails.email || displaySwapDetails.swapDescription)">
                <div class="mb-2">
                  <strong>LOCATION:</strong> {{ displaySwapDetails.location }}
                </div>
                <div class="mb-2">
                  <strong>YOUR EMAIL:</strong> {{ displaySwapDetails.email }}
                </div>
                <div class="mb-0">
                  <strong>SWAP DESCRIPTION:</strong> {{ displaySwapDetails.swapDescription }}
                </div>
              </div>

              <!-- Unavailable Notice -->
              <div v-if="unavailable" class="alert alert-warning d-flex align-items-center gap-2 mb-4">
                <i class="material-icons">info</i>
                <span>{{ unavailableMessage }}</span>
              </div>

              <!-- User Message -->
              <div class="mb-4">
                <p class="mb-2">Hello <strong>{{ displayUserName }}</strong>,</p>
                <p class="mb-2 text-success fw-bold" v-if="!unavailable">{{ displayMessage }}</p>
                <p class="mb-2" v-if="displayAdditionalMessage">{{ displayAdditionalMessage }}</p>
                <p class="mb-0">Thank you.</p>
              </div>

              <MaterialButton v-if="!unavailable && !declineSuccess" color="danger" :disabled="declining" @click="handleDecline">
                {{ declining ? 'Processing...' : 'Decline Assignment' }}
              </MaterialButton>
              <div v-if="declineSuccess" class="alert alert-success d-flex align-items-center gap-2 mb-3">
                <i class="material-icons">check_circle</i>
                <span>The swap request has been declined successfully.</span>
              </div>

              <!-- Divider -->
              <hr class="my-4" />

              <!-- Did You Know Section -->
              <div class="mb-4"
                v-if="showDidYouKnow && (displayDidYouKnowText || (displayDidYouKnowSteps && displayDidYouKnowSteps.length > 0))">
                <h5 class="fw-bold mb-3">Did You Know?</h5>
                <p class="mb-3" v-if="displayDidYouKnowText">{{ displayDidYouKnowText }}</p>
                <p class="mb-2" v-if="displayDidYouKnowSteps && displayDidYouKnowSteps.length > 0">To setup an external
                  calendar, follow the steps below:</p>
                <ul class="mb-3" v-if="displayDidYouKnowSteps && displayDidYouKnowSteps.length > 0">
                  <li v-for="(step, index) in displayDidYouKnowSteps" :key="index" class="mb-2">{{ step }}</li>
                </ul>
              </div>

              <!-- Login Link -->
              <router-link to="/sign-in">
                <MaterialButton color="success">
                  Click here to Login
                </MaterialButton>
              </router-link>
            </template>

          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { useToast } from 'vue-toast-notification'
import MaterialButton from '@/components/common/MaterialButton.vue'
import api from '@/config/api'

// route + toast
const route = useRoute()
const toast = useToast()

// state
const loading = ref(true)
const swapDetails = ref({
  location: '',
  email: '',
  swapDescription: ''
})
const userName = ref('')
const message = ref('')
const additionalMessage = ref('')
const showDidYouKnow = ref(true)
const didYouKnowText = ref('')
const didYouKnowSteps = ref([])
const declining = ref(false)
const declineSuccess = ref(false)
const unavailable = ref(false)
const unavailableMessage = ref('')

// mock fallback data (kept from your original mock component)
const mockSwapDetails = {
  location: 'CS Test Church -> Chicago main Branch',
  email: 'Barbara_manchee@noemail.churchscheduling.com',
  swapDescription: 'Konadu A (Konadu_A_550@noemail.churchscheduling.com) requested to swap current assignment [Usher on Dec 20, 2025 10:00:00 AM] with Barbara Manchandurana (Barbara_manchee@noemail.churchscheduling.com) for [Usher on Jan 11, 2026 10:00:00 AM].'
}
const mockUserName = 'Barbara'
const mockDidYouKnowText = 'You can setup external calendars like google, yahoo to display your upcoming assignments.'
const mockDidYouKnowSteps = [
  "log into the application and click on the 'my Profile/Preferences' button from the Home page",
  "copy the custom 'iCal URL' link",
  "paste the copied link into the appropriate calendar feed url field for the external calendar"
]

// computed display values (prefer API response; fallback to mock)
const displaySwapDetails = computed(() =>
  swapDetails.value && (swapDetails.value.location || swapDetails.value.email || swapDetails.value.swapDescription)
    ? swapDetails.value
    : mockSwapDetails
)
const displayUserName = computed(() => userName.value || mockUserName)
const displayMessage = computed(() => message.value || '')
const displayAdditionalMessage = computed(() => additionalMessage.value || '')
const displayDidYouKnowText = computed(() => didYouKnowText.value || mockDidYouKnowText)
const displayDidYouKnowSteps = computed(() => (didYouKnowSteps.value && didYouKnowSteps.value.length > 0) ? didYouKnowSteps.value : mockDidYouKnowSteps)

// Fetch swap email response details
const fetchSwapEmailResponseDetails = async () => {
  const encodedString = route.params.encodedString
  if (!encodedString) {
    console.error('No encodedString found in route params')
    loading.value = false
    return
  }

  try {
    loading.value = true
    const response = await api.get(`/account/swapemailresponse-details/cs/${encodedString}`)

    if (response.data) {
      const data = response.data

      // Map API response to component state
      if (data.assignmentDetails) {
        swapDetails.value = {
          location: data.assignmentDetails.location || '',
          email: data.assignmentDetails.email || '',
          swapDescription: data.assignmentDetails.swap_description || ''
        }
      }

      userName.value = data.username || ''
      message.value = data.message || ''
      additionalMessage.value = data.additionalMessage || ''
      if (data.unavailable) {
        unavailable.value = true
        unavailableMessage.value = data.message || 'This swap request is no longer available.'
      }
      if (data.didYouKnowText != null) didYouKnowText.value = data.didYouKnowText
      if (data.didYouKnowSteps && Array.isArray(data.didYouKnowSteps)) didYouKnowSteps.value = data.didYouKnowSteps
    }
  } catch (error) {
    console.error('Error fetching swap email response details:', error)
  } finally {
    loading.value = false
  }
}

// Decline handler
const handleDecline = async () => {
  if (declining.value) return
  declining.value = true
  try {
    const response = await api.get(`/account/swapemailresponse/cs/${route.params.encodedString}`)
    if (response.data?.unavailable) {
      unavailable.value = true
      unavailableMessage.value = response.data.message || 'This swap request is no longer available.'
    } else {
      declineSuccess.value = true
      toast.success('Swap request declined successfully.', { position: 'top-right' })
    }
  } catch (error) {
    const errData = error.response?.data
    if (errData?.unavailable) {
      unavailable.value = true
      unavailableMessage.value = errData.message || 'This swap request is no longer available.'
    } else {
      toast.error(errData?.message || 'Failed to decline swap request.', { position: 'top-right' })
    }
  } finally {
    declining.value = false
  }
}

// On mount: confirm swap (show toast), then fetch details
onMounted(async () => {
  await fetchSwapEmailResponseDetails()
})
</script>